import { yupResolver } from '@hookform/resolvers/yup';
import { CustomerModel, CustomerMutationInput } from 'api-hooks/customer';
import { CircleSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import { UserStatusEnum, EnumList } from 'common/constants/enum';
import formSetErrors from 'common/helpers/form-setError';
import { themedStyled } from 'common/styles/styletron.theme';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, FormState, Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import FormHeader from 'components/widgets/form-header';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import UserNameField from 'legacy-modules/master/common/user-name-field';
import EnumSelectInput from 'legacy-modules/master/select-input/enum';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  OneThirdContainer,
  SectionContainer as RawSectionContainer,
  SeparatorVertical,
  Title,
} from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import ActionComponent from './action-component';
import BillingAddressCard from './billing-address-card';
import { CustomerFormSchema, CustomerFormType } from './form-type';
import SaleSystemCard from './sale-system-card';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  customer?: CustomerModel;
  onSubmit: (
    input: CustomerMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<CustomerModel | undefined>;
}

export default function CustomerForm(props: Props) {
  const { t } = useTranslation();
  const { customer } = props;
  const { asPath } = useRouter();

  const { navigate } = useNavigation();

  const defaultValues = useGetDefaultValues(customer);

  const methods = useForm<CustomerFormType>({
    resolver: yupResolver(CustomerFormSchema()),
    defaultValues,
  });

  const onSubmit = React.useCallback(
    async (values: CustomerFormType) => {
      try {
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );

  const onViewMutation = () => {
    navigate(NavigationRoutes.CustomerMutationReport, {
      filter: {
        customer: customer!.id!,
      },
    });
  };

  const isSafe = customer?.status === UserStatusEnum.Safe;

  const permission = useGetCrudPermission('customers');
  const customViewName =
    customer && [t('common:customer'), customer?.name].join(' - ');

  const onRenderComponent = !!customer && !methods.formState.isSubmitting && (
    <>
      <ActionComponent customer={customer} />
      <Separator gap={4} />
    </>
  );

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!customer}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormState>
        {({ editable }) => (
          <FormHeader
            data={customer}
            noDelete
            noEdit={!permission.update}
            title={
              customer ? (
                <>
                  <FullContainer
                    $style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                    }}
                  >
                    <Title variant="HeadingXSmall">{customViewName}</Title>
                    {!isSafe && (
                      <>
                        <Separator gap={16} />
                        <CircleSVG />
                      </>
                    )}
                  </FullContainer>
                </>
              ) : (
                t('common:add_extra', { extra: t('common:customer') })
              )
            }
            onRenderComponent={editable ? undefined : onRenderComponent}
          />
        )}
      </FormState>
      <div>
        <CardSection>
          <Text variant="HeadingXSmall">{t('customer:customer_detail')}</Text>
          <SeparatorVertical $margin={16} />
          <SectionContainer>
            <HalfContainer>
              <Input
                type="text-area"
                name="description"
                label={t('common:description')}
                placeholder={t('common:description')}
              />
            </HalfContainer>
            <HalfContainer
              $style={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <SectionContainer>
                <HalfContainer>
                  <EnumSelectInput
                    name="type"
                    label={t('customer:customer_type')}
                    placeholder={t('customer:customer_type')}
                    enumType={EnumList.CustomerType}
                    clearable={false}
                    required
                  />
                </HalfContainer>
                <HalfContainer>
                  <EnumSelectInput
                    name="religion"
                    label={t('customer:religion')}
                    placeholder={t('customer:religion')}
                    enumType={EnumList.Religion}
                  />
                </HalfContainer>
              </SectionContainer>
            </HalfContainer>
          </SectionContainer>
          <SectionContainer>
            <HalfContainer>
              <UserNameField titleClass={EnumList.TitleType} />
            </HalfContainer>
            <HalfContainer>
              <Input
                type="text"
                name="alias"
                label={t('customer:alias')}
                placeholder={t('customer:customer_alias')}
                fontCase="TitleCase"
              />
            </HalfContainer>
          </SectionContainer>
          <SectionContainer>
            <OneThirdContainer>
              <Input
                type="email"
                name="email"
                label={t('common:email')}
                placeholder={t('common:email')}
              />
            </OneThirdContainer>
            <OneThirdContainer>
              <Input
                type="tel"
                name="phone"
                label={t('common:phone_number')}
                placeholder={t('common:phone_number')}
                required
              />
            </OneThirdContainer>
            <OneThirdContainer>
              <Input
                type="tel"
                name="phone_2"
                label={t('cruise:cellphone_number')}
                placeholder={t('cruise:cellphone_number')}
              />
            </OneThirdContainer>
          </SectionContainer>
          <FullContainer>
            <Input
              type="text"
              name="address"
              label={t('common:address')}
              placeholder={t('common:address')}
              required
              noMargin
              fontCase="TitleCase"
            />
          </FullContainer>
        </CardSection>
        <SeparatorVertical $margin={24} />
        <BillingAddressCard />
        <SeparatorVertical $margin={24} />
        {customer && (
          <>
            <CardSection>
              <SectionContainer>
                <Text variant="HeadingXSmall">
                  {t('customer:customer_balance')}
                </Text>
                <Button buttonType="tertiary" onClick={onViewMutation}>
                  {t('common:view_mutation')}
                </Button>
              </SectionContainer>
              <SeparatorVertical $margin={16} />
              <FullContainer>
                <Input
                  type="number"
                  name="balance"
                  label={t('customer:balance')}
                  placeholder={t('customer:customer_balance')}
                  disabled={!!customer}
                  noMargin
                />
              </FullContainer>
            </CardSection>
            <SeparatorVertical $margin={24} />
          </>
        )}
        <SaleSystemCard />
        <SeparatorVertical $margin={32} />
      </div>
    </Form>
  );
}

const SectionContainer = themedStyled(RawSectionContainer, {
  width: '100%',
  alignItems: 'center',
});
